import { useEffect } from 'react'
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import Layout from '@/components/Layout'
import ProtectedRoute from '@/components/ProtectedRoute'
import Landing from '@/pages/Landing'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import EmailSignupPage from '@/pages/EmailSignupPage'
import ConfirmEmailPage from '@/pages/ConfirmEmailPage'
import OnboardingPage from '@/pages/OnboardingPage'
import DashboardPage from '@/pages/DashboardPage'
import ActivityFeedPage from '@/pages/ActivityFeedPage'
import SessionDetailPage from '@/pages/SessionDetailPage'
import IncentivesPage from '@/pages/IncentivesPage'
import GoalsPage from '@/pages/GoalsPage'
import ExercisePickerPage from '@/pages/ExercisePickerPage'
import WordDecodePage from '@/pages/exercises/WordDecodePage'
import SynonymMatchPage from '@/pages/exercises/SynonymMatchPage'
import SynonymPairsPage from '@/pages/exercises/SynonymPairsPage'
import SpellingGamePage from '@/pages/exercises/SpellingGamePage'
import IntervalRecognitionPage from '@/pages/exercises/IntervalRecognitionPage'
import EarTrainingPage from '@/pages/exercises/EarTrainingPage'
import MelodicDictationPage from '@/pages/exercises/MelodicDictationPage'
import ActPracticePage from '@/pages/exercises/ActPracticePage'
import ActReviewPage from '@/pages/exercises/ActReviewPage'
import HarmonicDictationPage from '@/pages/exercises/HarmonicDictationPage'
import NoteNamingPage from '@/pages/exercises/NoteNamingPage'
import GameSettingsPage from '@/pages/GameSettingsPage'
import ScreenTimePage from '@/pages/ScreenTimePage'
import TvManagementPage from '@/pages/TvManagementPage'
import MagicLoginPage from '@/pages/MagicLoginPage'
import SettingsPage from '@/pages/SettingsPage'
import AdminPage from '@/pages/AdminPage'
import MagnumWorkshopPage from '@/pages/MagnumWorkshopPage'
import InviteAcceptPage from '@/pages/InviteAcceptPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'

function useAnalyticsIdentify() {
  const { user } = useAuth()

  useEffect(() => {
    if (!user) {
      posthog.reset()
      Sentry.setUser(null)
      return
    }

    if (user.type === 'parent') {
      const id = `parent-${user.parent.id}`
      posthog.identify(id, { type: 'parent', email: user.parent.email })
      Sentry.setUser({ id, email: user.parent.email })
    } else {
      const id = `child-${user.child.id}`
      posthog.identify(id, { type: 'child', username: user.child.username })
      Sentry.setUser({ id, username: user.child.username })
    }
  }, [user])
}

function AppRoutes() {
  const { user, loading } = useAuth()
  useAnalyticsIdentify()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <Switch>
      <Route
        exact
        path="/"
        render={() => {
          if (!user) return <Landing />
          return <Redirect to={user.type === 'parent' ? '/dashboard' : '/practice'} />
        }}
      />
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={EmailSignupPage} />
      <Route path="/signup/legacy" component={SignupPage} />
      <Route path="/confirm" component={ConfirmEmailPage} />
      <Route path="/invite" component={InviteAcceptPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/magic-login" component={MagicLoginPage} />
      <ProtectedRoute path="/onboarding" requiredType="parent" component={OnboardingPage} />
      <Redirect from="/child-login" to="/login" />
      <ProtectedRoute path="/dashboard/children/:childId/activity/:sessionId" requiredType="parent" component={SessionDetailPage} />
      <ProtectedRoute path="/dashboard/children/:childId/activity" requiredType="parent" component={ActivityFeedPage} />
      <ProtectedRoute path="/dashboard/children/:childId/incentives" requiredType="parent" component={IncentivesPage} />
      <ProtectedRoute path="/dashboard/children/:childId/goals" requiredType="parent" component={GoalsPage} />
      <ProtectedRoute path="/dashboard/children/:childId/game-settings" requiredType="parent" component={GameSettingsPage} />
      <ProtectedRoute path="/dashboard/children/:childId/screen-time" requiredType="parent" component={ScreenTimePage} />
      <ProtectedRoute path="/settings/tvs" requiredType="parent" component={TvManagementPage} />
      <ProtectedRoute path="/settings" requiredType="parent" component={SettingsPage} />
      <ProtectedRoute path="/admin/magnum-workshop" requiredType="parent" component={MagnumWorkshopPage} />
      <ProtectedRoute path="/admin" requiredType="parent" component={AdminPage} />
      <ProtectedRoute path="/dashboard" requiredType="parent" component={DashboardPage} />
      <ProtectedRoute exact path="/practice" requiredType="child" component={ExercisePickerPage} />
      <ProtectedRoute path="/practice/decode" requiredType="child" component={WordDecodePage} />
      <ProtectedRoute path="/practice/synonym-match" requiredType="child" component={SynonymMatchPage} />
      <ProtectedRoute path="/practice/synonym-pairs" requiredType="child" component={SynonymPairsPage} />
      <ProtectedRoute path="/practice/spelling" requiredType="child" component={SpellingGamePage} />
      <ProtectedRoute path="/practice/interval-recognition" requiredType="child" component={IntervalRecognitionPage} />
      <ProtectedRoute path="/practice/ear-training" requiredType="child" component={EarTrainingPage} />
      <ProtectedRoute path="/practice/melodic-dictation" requiredType="child" component={MelodicDictationPage} />
      <ProtectedRoute path="/practice/harmonic-dictation" requiredType="child" component={HarmonicDictationPage} />
      <ProtectedRoute path="/practice/note-naming" requiredType="child" component={NoteNamingPage} />
      <ProtectedRoute path="/practice/act/review" requiredType="child" component={ActReviewPage} />
      <ProtectedRoute exact path="/practice/act" requiredType="child" component={ActPracticePage} />
    </Switch>
  )
}

if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
  })
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center"><p>Something went wrong. Please refresh the page.</p></div>}>
      <Router>
        <AuthProvider>
          <Layout>
            <AppRoutes />
          </Layout>
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  )
}
